import { c as _c } from "react/compiler-runtime";
import type { PullRequestPreview } from './types';
import { cn } from '@/lib/utils';

/** Enters after the card, so each prompt shows the moment happening rather than a still. */
const ENTER = 'animate-in fade-in-0 fill-mode-both duration-500 motion-reduce:animate-none';
function CubicMark(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("grid size-6 shrink-0 place-items-center rounded-md bg-black", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="size-2.5 rounded-[3px] border-2 border-white" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <span aria-hidden="true" className={t1}>{t2}</span>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function Frame(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div aria-hidden="true" className="relative overflow-hidden rounded-lg border bg-muted/40 p-2.5 text-[11px] leading-snug">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/** The menu bar, with the tray popover dropping open on the person's own waiting pull requests. */
export function TrayVisual(t0) {
  const $ = _c(14);
  const {
    waiting,
    waitingCount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="size-2.5 rounded-[3px] border-[1.5px] border-current" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== waitingCount) {
    t2 = <div className="-mx-2.5 -mt-2.5 mb-2 flex justify-end border-b bg-background/60 px-2.5 py-1"><span className="inline-flex items-center gap-1 font-semibold tabular-nums">{t1}{waitingCount}</span></div>;
    $[1] = waitingCount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(ENTER, "ml-auto w-[88%] origin-top-right rounded-md border bg-card shadow-md zoom-in-95 slide-in-from-top-2 [animation-delay:350ms]");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `Needs you · ${waitingCount}`;
  let t5;
  if ($[4] !== t4) {
    t5 = <div className="border-b px-2 py-1 font-medium">{t4}</div>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== waiting) {
    t6 = waiting.map(_temp);
    $[6] = waiting;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <div className={t3}>{t5}{t6}</div>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t7) {
    t8 = <Frame>{t2}{t7}</Frame>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}

/** A desktop notification with its buttons, the way the app shows review requests. */
function _temp(pr) {
  return <div key={`${pr.owner}/${pr.repo}#${pr.number}`} className="flex gap-2 px-2 py-1"><span className="min-w-0 flex-1 truncate">{pr.title}</span><span className="shrink-0 text-muted-foreground">{`#${pr.number}`}</span></div>;
}
export function NotificationVisual(t0) {
  const $ = _c(15);
  const {
    title,
    body,
    button
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(ENTER, "ml-auto grid w-[92%] grid-cols-[auto_1fr] gap-2 rounded-lg border bg-card p-2 shadow-md slide-in-from-right-6 [animation-delay:300ms]");
    t2 = <CubicMark />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <div className="truncate font-medium">{title}</div>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== body) {
    t4 = <div className="truncate text-muted-foreground">{body}</div>;
    $[4] = body;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="min-w-0">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="rounded border py-1 text-center">Open</span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== button) {
    t7 = <div className="col-span-2 grid grid-cols-2 gap-1.5">{t6}<span className="rounded bg-primary py-1 text-center text-primary-foreground">{button}</span></div>;
    $[10] = button;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <Frame><div className={t1}>{t2}{t5}{t7}</div></Frame>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}

/** The app icon picking up a badge for the next review request. */
export function BadgeVisual() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="text-muted-foreground">0 PRs need your review</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Frame><div className="flex items-center justify-center gap-3 py-2">{t0}<span className="relative"><CubicMark className="size-9 rounded-xl" /><span className={cn(ENTER, "absolute -right-1.5 -top-1.5 grid size-4 place-items-center rounded-full bg-red-500 text-[10px] font-semibold text-white zoom-in-50 [animation-delay:700ms]")}>1</span></span></div></Frame>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SlackMessage(t0) {
  const $ = _c(11);
  const {
    author,
    children,
    delay
  } = t0;
  let t1;
  if ($[0] !== delay) {
    t1 = cn(ENTER, "flex gap-2 slide-in-from-bottom-2", delay);
    $[0] = delay;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mt-0.5 size-5 shrink-0 rounded bg-muted-foreground/40" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== author) {
    t3 = <div className="font-semibold">{author}</div>;
    $[3] = author;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t3) {
    t4 = <div className="min-w-0 flex-1">{t3}{children}</div>;
    $[5] = children;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** The pull request link pasted into Slack, unfurled into a card. */
export function UnfurlVisual(t0) {
  const $ = _c(13);
  const {
    pullRequest
  } = t0;
  const t1 = `cubic.dev/pr/${pullRequest.owner}/${pullRequest.repo}/pull/${pullRequest.number}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className="truncate text-primary">{t1}</div>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn(ENTER, "mt-1 border-l-2 border-primary pl-2 slide-in-from-left-2 [animation-delay:650ms]");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== pullRequest.title) {
    t4 = <div className="truncate font-medium">{pullRequest.title}</div>;
    $[3] = pullRequest.title;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `${pullRequest.repo} #${pullRequest.number} · reviewed by cubic`;
  let t6;
  if ($[5] !== t5) {
    t6 = <div className="text-muted-foreground">{t5}</div>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t4 || $[8] !== t6) {
    t7 = <div className={t3}>{t4}{t6}</div>;
    $[7] = t4;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t2 || $[11] !== t7) {
    t8 = <Frame><SlackMessage author="you" delay="[animation-delay:150ms]">{t2}{t7}</SlackMessage></Frame>;
    $[10] = t2;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}

/** A Slack channel for the pull request, with its reviewers joining. */
export function ChannelVisual(t0) {
  const $ = _c(10);
  const {
    pullRequest
  } = t0;
  let t1;
  if ($[0] !== pullRequest.repo) {
    t1 = pullRequest.repo.toLowerCase();
    $[0] = pullRequest.repo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `#pr-${t1}-${pullRequest.number}`;
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="mb-1.5 truncate border-b pb-1 font-semibold">{t2}</div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className={cn(ENTER, "text-muted-foreground [animation-delay:200ms]")}>Everyone on the pull request joined</div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = `Updates on ${pullRequest.title} post here`;
  let t6;
  if ($[5] !== t5) {
    t6 = <div className="grid gap-1.5">{t4}<SlackMessage author="cubic" delay="[animation-delay:700ms]"><div className="truncate text-muted-foreground">{t5}</div></SlackMessage></div>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t6) {
    t7 = <Frame>{t3}{t6}</Frame>;
    $[7] = t3;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  return t7;
}